import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const sections = [
  {
    title: "1. Uso del Servicio",
    content: [
      "La aplicación Serena está diseñada para ofrecer una experiencia de bienestar a través de contenido digital asociado a nuestras velas aromáticas premium. Al utilizar nuestra app, usted acepta:",
      "• Usar la aplicación únicamente para fines personales y no comerciales.",
      "• No reproducir, duplicar, copiar, vender o explotar cualquier parte del servicio sin autorización expresa.",
      "• No intentar acceder a áreas restringidas de la aplicación ni interferir con su funcionamiento.",
      "• Proporcionar información veraz y actualizada al registrarse.",
      "Nos reservamos el derecho de suspender o cancelar su acceso si detectamos un uso indebido del servicio.",
    ],
  },
  {
    title: "2. Propiedad Intelectual",
    content: [
      "Todo el contenido de la aplicación Serena, incluyendo pero no limitado a: textos, gráficos, logotipos, íconos, imágenes, clips de audio, afirmaciones, rituales, meditaciones guiadas y software, es propiedad exclusiva de Serena o sus licenciantes.",
      "Queda estrictamente prohibida la reproducción, distribución, modificación o uso no autorizado de cualquier contenido sin el consentimiento previo por escrito de Serena.",
      "Las marcas, nombres comerciales y logotipos mostrados en la aplicación son propiedad de Serena y no podrán ser utilizados sin autorización.",
    ],
  },
  {
    title: "3. Privacidad y Datos Personales",
    content: [
      "Serena se compromete a proteger su privacidad. Al usar nuestra aplicación, recopilamos ciertos datos necesarios para brindarle el servicio:",
      "• Información de registro: nombre, correo electrónico.",
      "• Datos de uso: interacciones con el contenido, preferencias de bienestar, historial de rituales.",
      "• Datos del dispositivo: modelo, sistema operativo, identificadores únicos.",
      "Esta información se utiliza exclusivamente para personalizar su experiencia, mejorar nuestros servicios y enviarle comunicaciones relevantes. No compartimos sus datos personales con terceros sin su consentimiento, salvo cuando sea requerido por ley.",
      "Para más detalles, consulte nuestra Política de Privacidad completa.",
    ],
  },
  {
    title: "4. Compras y Pagos",
    content: [
      "La aplicación puede ofrecer contenido premium, suscripciones o compras dentro de la app. Al realizar una compra:",
      "• Los pagos se procesan a través de la plataforma correspondiente (Google Play Store o Apple App Store).",
      "• Los precios están expresados en pesos mexicanos (MXN) e incluyen impuestos aplicables.",
      "• Las suscripciones se renuevan automáticamente a menos que se cancelen antes del final del periodo de facturación.",
      "• Las solicitudes de reembolso se gestionan conforme a las políticas de la tienda de aplicaciones correspondiente.",
      "Serena se reserva el derecho de modificar los precios de sus servicios con previo aviso a los usuarios.",
    ],
  },
  {
    title: "5. Contenido de Bienestar",
    content: [
      "El contenido de bienestar proporcionado por Serena (afirmaciones, sonidos, rituales, meditaciones) tiene fines informativos y de relajación.",
      "• No sustituye el consejo médico, psicológico o terapéutico profesional.",
      "• No garantizamos resultados específicos de salud o bienestar.",
      "• Si experimenta alguna condición de salud, consulte a un profesional antes de utilizar nuestros servicios.",
    ],
  },
  {
    title: "6. Limitación de Responsabilidad",
    content: [
      "Serena proporciona la aplicación \"tal cual\" y \"según disponibilidad\". En la medida permitida por la ley aplicable:",
      "• No garantizamos que el servicio sea ininterrumpido, seguro o libre de errores.",
      "• No somos responsables por daños indirectos, incidentales, especiales o consecuentes derivados del uso de la aplicación.",
      "• Nuestra responsabilidad total estará limitada al monto que usted haya pagado por el servicio en los últimos 12 meses.",
      "• No nos responsabilizamos por el contenido generado por usuarios ni por servicios de terceros vinculados a la aplicación.",
    ],
  },
  {
    title: "7. Modificaciones",
    content: [
      "Serena se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones serán efectivas al publicarse en la aplicación.",
      "Le notificaremos sobre cambios significativos a través de la aplicación o por correo electrónico. El uso continuado del servicio después de la publicación de los cambios constituirá su aceptación de los términos modificados.",
      "Si no está de acuerdo con los nuevos términos, deberá dejar de utilizar la aplicación y solicitar la eliminación de su cuenta.",
    ],
  },
  {
    title: "8. Ley Aplicable",
    content: [
      "Estos Términos y Condiciones se regirán e interpretarán de conformidad con las leyes de los Estados Unidos Mexicanos.",
      "Cualquier controversia derivada del uso de la aplicación se someterá a la jurisdicción de los tribunales competentes de la Ciudad de México.",
    ],
  },
  {
    title: "9. Eliminación de Cuenta y Datos",
    content: [
      "Usted tiene derecho a solicitar la eliminación completa de su cuenta y todos los datos personales asociados en cualquier momento.",
      "Al solicitar la eliminación, se borrarán de forma permanente e irreversible:",
      "• Información de su cuenta (nombre, correo electrónico).",
      "• Historial de rituales y preferencias de bienestar.",
      "• Datos de uso y configuración de la aplicación.",
      "• Cualquier contenido generado dentro de la app.",
      "Para solicitar la eliminación de su cuenta y datos, utilice nuestro formulario de solicitud disponible en: /eliminar-cuenta",
      "Procesaremos su solicitud en un plazo máximo de 10 días hábiles y le enviaremos una confirmación por correo electrónico.",
    ],
  },
  {
    title: "10. Contacto",
    content: [
      "Si tiene preguntas o inquietudes sobre estos Términos y Condiciones, puede contactarnos a través de:",
      "• Correo electrónico: hola@serenacandles.com.mx",
      "• Sitio web: serenacandles.com.mx",
      "Nos comprometemos a responder sus consultas en un plazo máximo de 10 días hábiles.",
    ],
  },
];

export default function TermsAndConditions() {
  return (
    <div
      className="min-h-screen bg-[#050505] text-white relative noise-overlay"
      data-testid="terms-page"
    >
      {/* Decorative gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/3 blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#D4AF37] transition-colors duration-300 mb-12 group"
            aria-label="Volver al inicio"
          >
            <ArrowLeft
              size={16}
              className="group-hover:-translate-x-1 transition-transform duration-300"
            />
            Volver al inicio
          </Link>
        </motion.div>

        {/* Title */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1 } },
          }}
        >
          <motion.h1
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-4 text-white"
          >
            Términos y{" "}
            <span className="italic text-[#D4AF37]">Condiciones</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="text-sm text-white/30 mb-16 tracking-wide"
          >
            Última actualización: 10 de marzo de 2026
          </motion.p>
        </motion.div>

        {/* Intro */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-base md:text-lg text-white/60 leading-relaxed">
            Bienvenido a Serena. Al descargar, acceder o utilizar nuestra
            aplicación móvil, usted acepta estos Términos y Condiciones en su
            totalidad. Si no está de acuerdo con alguno de estos términos, le
            solicitamos no utilizar nuestros servicios.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => (
            <motion.section
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeUp}
              className="group"
            >
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight mb-5 text-white/90 group-hover:text-[#D4AF37] transition-colors duration-500">
                {section.title}
              </h2>
              <div className="space-y-3 pl-0 md:pl-4 border-l border-white/5 md:border-l-2 md:border-[#D4AF37]/10">
                {section.content.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className="text-sm md:text-base text-white/50 leading-relaxed pl-4"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Divider */}
        <div className="my-20 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

        {/* Footer note */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center"
        >
          <p className="text-xs text-white/20 mb-6">
            © 2025 Serena. Todos los derechos reservados. Hecho en México.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors duration-300"
            aria-label="Volver al inicio"
          >
            <ArrowLeft size={14} />
            Volver al inicio
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
